import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip,
} from '@mui/material'
import PaletteIcon from '@mui/icons-material/Palette'
import CheckIcon from '@mui/icons-material/Check'
import { BrandPaletteEntry, BrandTheme, BrandThemeHistoryEntry } from '../../types/gotypes'
import ThemeManagerDialog from '../brand-palette/ThemeManagerDialog'
import type { WhoToAsk } from '../../utils/permissionReason'
import { resolvePagePalette, themeLookupFromList } from '../../utils/brandTheme'

const JOB_DEFAULT_VALUE = '__job_default__'

export interface PageBrandColorsPanelProps {
  open: boolean
  onClose: () => void
  pageName: string
  /** The page's brand_palette_key — a BrandTheme id, or undefined for job default. */
  themeId?: string
  /** Selecting applies IMMEDIATELY (live reference, #1182): undefined clears back to job default. */
  onSelectTheme: (themeId: string | undefined) => void
  themes: BrandTheme[]
  themesLoading?: boolean
  /** The job's working palette — the fallback layer for brands the theme does not name. */
  jobPalette?: Record<string, BrandPaletteEntry>
  // Manager affordances: theme CRUD (ThemeManagerDialog).
  canManageThemes?: boolean
  onSaveTheme?: (id: string, name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onCreateTheme?: (name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onDeleteTheme?: (id: string) => Promise<void>
  /** Three-tier ownership (#1266), passed through to the theme manager. */
  currentUserEmail?: string
  canPromoteThemes?: boolean
  onPromoteTheme?: (id: string) => Promise<void>
  onDemoteTheme?: (id: string) => Promise<void>
  onLoadThemeHistory?: (id: string) => Promise<BrandThemeHistoryEntry[]>
  /** Who to ask about a theme the user cannot change (useWhoToAsk at the page). */
  whoToAsk?: WhoToAsk
  /**
   * "Reset all charts on page" (#1175, Matt 1 Sep — replaces "Set as job
   * default"): clears every chart-level theme override on this page so all
   * its charts follow the page theme again. Returns the number of charts
   * reset. The job default palette no longer has an editor anywhere — themes
   * supersede it, by the same instruction.
   */
  onResetCharts?: () => number
}

/**
 * Page brand colors (#1182) — opened from the palette icon on a page tab.
 *
 * The page stores a theme ID, never a copy of the colours: picking a theme
 * here is picking a live reference, so editing that theme later recolours this
 * page (and every other page that uses it) without reassignment. The swatch
 * list below the selector shows the RESOLVED result: the theme's colours over
 * the job palette's, because the job palette supplies every brand the theme
 * does not name.
 */
const PageBrandColorsPanel: React.FC<PageBrandColorsPanelProps> = ({
  open,
  onClose,
  pageName,
  themeId,
  onSelectTheme,
  themes,
  themesLoading,
  jobPalette,
  canManageThemes = false,
  onSaveTheme,
  onCreateTheme,
  onDeleteTheme,
  currentUserEmail,
  canPromoteThemes,
  onPromoteTheme,
  onDemoteTheme,
  onLoadThemeHistory,
  whoToAsk,
  onResetCharts,
}) => {
  const [themeManagerOpen, setThemeManagerOpen] = useState(false)
  const [resetCount, setResetCount] = useState<number | null>(null)

  const selectedTheme = themeId ? themes.find((t) => t.id === themeId) : undefined
  // A stored id whose theme no longer exists (deleted) — say so rather than
  // silently showing the job default under a broken selector.
  const themeMissing = Boolean(themeId) && !themesLoading && !selectedTheme

  const resolved = resolvePagePalette({ brand_palette_key: themeId }, themeLookupFromList(themes), jobPalette)
  const themedBrands = new Set(Object.keys(selectedTheme?.palette || {}))


  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaletteIcon fontSize="small" color="info" />
        Theme — {pageName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Select theme</InputLabel>
            <Select
              value={selectedTheme ? selectedTheme.id : JOB_DEFAULT_VALUE}
              label="Select theme"
              onChange={(e) => {
                const v = e.target.value
                onSelectTheme(v === JOB_DEFAULT_VALUE ? undefined : v)
                setResetCount(null)
              }}
            >
              <MenuItem value={JOB_DEFAULT_VALUE}>
                <em>Job default colours</em>
              </MenuItem>
              {themes.map((theme) => (
                <MenuItem key={theme.id} value={theme.id}>
                  <ListItemText
                    primary={theme.name}
                    secondary={`${Object.keys(theme.palette).length} colours`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {themeMissing && (
            <Typography variant="caption" color="warning.main">
              This page's theme no longer exists — the job default colours are showing.
              Pick a theme, or Job default colours to clear the reference.
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            Themes update live: editing a theme recolours every page and chart that uses it.
            Brands the theme doesn't name fall back to the job default colours.
          </Typography>

          {resolved && Object.keys(resolved).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(resolved).map(([brandName, entry]) => (
                <Box key={brandName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: entry.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                    {brandName}
                  </Typography>
                  {selectedTheme && !themedBrands.has(brandName) && (
                    <Chip label="job default" size="small" variant="outlined" />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {themes.length > 0
                ? 'No colours set for this job yet — pick a theme above.'
                : 'No themes yet — create one with Manage Themes below.'}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {canManageThemes && (
              <Button
                size="small"
                color="info"
                startIcon={<PaletteIcon fontSize="small" />}
                onClick={() => setThemeManagerOpen(true)}
              >
                Manage Themes
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {onResetCharts && (
              <Button
                size="small"
                color="info"
                variant="outlined"
                onClick={() => setResetCount(onResetCharts())}
                startIcon={resetCount !== null ? <CheckIcon /> : undefined}
              >
                {resetCount !== null
                  ? `${resetCount} chart${resetCount === 1 ? '' : 's'} reset`
                  : 'Reset all charts on page'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info">
          Done
        </Button>
      </DialogActions>

      {canManageThemes && onSaveTheme && onDeleteTheme && (
        <ThemeManagerDialog
          open={themeManagerOpen}
          onClose={() => setThemeManagerOpen(false)}
          themes={themes}
          loading={themesLoading || false}
          onSaveTheme={onSaveTheme}
          onCreateTheme={onCreateTheme}
          onDeleteTheme={onDeleteTheme}
          currentUserEmail={currentUserEmail}
          canPromote={canPromoteThemes}
          onPromoteTheme={onPromoteTheme}
          onDemoteTheme={onDemoteTheme}
          onLoadThemeHistory={onLoadThemeHistory}
          whoToAsk={whoToAsk}
          onOpenTheme={(theme) => {
            onSelectTheme(theme.id)
            setThemeManagerOpen(false)
          }}
        />
      )}
    </Dialog>
  )
}

export default PageBrandColorsPanel
